import { InputConfig, getLanguages } from "@cpj-ai/panel-npm-package";
import { AuthAccountEducationDegreeChoices, useGetSkillsLazyQuery } from "@graphql/generated";
import { UseFormReturn } from "react-hook-form";

export const requiredDocumentsOptions = [
  { label: "Police Clearance Certificate", value: "Police Clearance Certificate" },
  { label: "Driver's License", value: "Driver's License" },
  { label: "Medical Examination (if applicable)", value: "Medical Examination (if applicable)" },
  {
    label: "Government-Issued ID (e.g., Passport, Driver’s License)",
    value: "Government-Issued ID (e.g., Passport, Driver’s License)",
  },
  { label: "Social Insurance Number (SIN)", value: "Social Insurance Number (SIN)" },
  {
    label: "Credit Check Authorization (for specific jobs)",
    value: "Credit Check Authorization (for specific jobs)",
  },
  { label: "Proof of Address", value: "Proof of Address" },
  {
    label: "Tax Information (e.g., T4 slip for reference)",
    value: "Tax Information (e.g., T4 slip for reference)",
  },
];

const MIN_AGE = 18;
const MAX_AGE = 65;

export const getInputConfig = ({ formHandler }: { formHandler: UseFormReturn }): InputConfig[] => {
  const formData = formHandler.getValues();
  return [
    {
      name: "skills",
      previewSize: 12,
      inputProps: {
        type: "searcher",
        label: "Skills & Expertise:",
        placeholder: "Skills & Expertise",
        useLazyQuery: useGetSkillsLazyQuery,
        dataPath: "common.skills",
        labelKey: "title",
        isMulti: true,
      },
      rules: {
        required: "This field is required.",
      },
    },
    {
      name: "degrees",
      previewSize: {
        sm: 12,
        md: 12,
        lg: 4,
      },
      inputProps: {
        type: "searcher",
        label: "Education:",
        placeholder: "Education",
        isMulti: true,
        labelKey: "label",
        valueKey: "value",
        items: [
          { label: "Certificate", value: AuthAccountEducationDegreeChoices.CERTIFICATE },
          { label: "Diploma", value: AuthAccountEducationDegreeChoices.DIPLOMA },
          { label: "Associate", value: AuthAccountEducationDegreeChoices.ASSOCIATE },
          { label: "Bachelors", value: AuthAccountEducationDegreeChoices.BACHELORS },
          { label: "Masters", value: AuthAccountEducationDegreeChoices.MASTERS },
          { label: "PhD", value: AuthAccountEducationDegreeChoices.PHD },
        ],
      },
    },
    {
      name: "minWorkExperienceYears",
      previewSize: {
        sm: 6,
        md: 6,
        lg: 2,
      },
      inputProps: {
        type: "number",
        label: "Min Work Experience",
        placeholder: "min experience",
      },
      rules: {
        required: "This field is required.",
        min: { value: 0, message: "Min Work Experience must be at least 0" },
      },
    },
    {
      name: "maxWorkExperienceYears",
      previewSize: {
        sm: 6,
        md: 6,
        lg: 2,
      },
      inputProps: {
        type: "number",
        label: "Max Work Experience",
        placeholder: "max experience",
      },
      rules: {
        required: "This field is required.",
        min: { value: 0, message: "Max Work Experience must be at least 0" },
        validate: (value: number) => {
          if (value <= formData.minWorkExperienceYears) {
            return "Max Work Experience must be greater than Min Work Experience";
          }
          return true;
        },
      },
    },
    {
      name: "languages",
      previewSize: {
        sm: 12,
        md: 12,
        lg: 4,
      },
      inputProps: {
        type: "searcher",
        label: "Required Languages:",
        placeholder: "Required Languages",
        isMulti: true,
        labelKey: "label",
        valueKey: "value",
        items: getLanguages(),
      },
    },
    {
      name: "nativeLanguages",
      previewSize: {
        sm: 12,
        md: 12,
        lg: 4,
      },
      inputProps: {
        type: "searcher",
        label: "Native & Fluent Languages:",
        placeholder: "Native Languages",
        isMulti: true,
        labelKey: "label",
        valueKey: "value",
        items: getLanguages(),
      },
    },
    {
      name: "minAge",
      previewSize: {
        sm: 6,
        md: 6,
        lg: 2,
      },
      inputProps: {
        type: "number",
        label: "Min Age Requirement",
        placeholder: "min age",
      },
      rules: {
        min: { value: MIN_AGE, message: `Min Age must be at least ${MIN_AGE}` },
        max: { value: MAX_AGE, message: `Min Age must be at most ${MAX_AGE}` },
        required: "This field is required.",
      },
    },
    {
      name: "maxAge",
      previewSize: {
        sm: 6,
        md: 6,
        lg: 2,
      },
      inputProps: {
        type: "number",
        label: "Max Age Requirement",
        placeholder: "max age",
      },
      rules: {
        min: { value: MIN_AGE, message: `Max Age must be at least ${MIN_AGE}` },
        max: { value: MAX_AGE, message: `Max Age must be at most ${MAX_AGE}` },
        validate: (value: number) => {
          if (value <= formData.minAge) {
            return "Max Age must be greater than Min Age";
          }
          return true;
        },
        required: "This field is required.",
      },
    },
    {
      name: "requiredDocuments",
      previewSize: {
        sm: 12,
        md: 12,
        lg: 4,
      },
      inputProps: {
        type: "searcher",
        label: "Required Documents:",
        placeholder: "Required Documents",
        isMulti: true,
        labelKey: "label",
        valueKey: "value",
        items: requiredDocumentsOptions,
      },
      rules: {
        required: "This field is required.",
      },
    },
    {
      name: "otherRequiredDocuments",
      previewSize: {
        sm: 12,
        md: 12,
        lg: 8,
      },
      inputProps: {
        type: "text",
        label: "Other Required Documents",
        placeholder: "EX: Doc 1, Doc 2",
      },
    },
    {
      name: "performanceExpectation",
      previewSize: {
        sm: 12,
      },
      inputProps: {
        defaultValue: "",
        type: "textarea",
        minRows: 10,
        label: "Performance Expectations:",
        placeholder: "Enter caption /description",
      },
      rules: {
        required: "This field is required.",
      },
    },
  ];
};
